import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getPreferences, updatePreferences, resubscribe } from '../api'
import { getClientId } from '../session'
import { focusAreaLimit, DAILY_DIGEST_TIERS } from '../tiers'
import Button from '../components/Button'
import Input from '../components/Input'
import Spinner from '../components/Spinner'
import { ErrorAlert, SuccessAlert } from '../components/Alert'
import EmailHealthBanner from '../components/EmailHealthBanner'
import { FOCUS_AREA_OPTIONS, US_STATES } from '../options'
import { grantRangeError } from '../grantRange'
import MissionHints from '../components/MissionHints'
import { useDocumentTitle } from '../components/PublicLayout'

export default function Preferences() {
  useDocumentTitle('Preferences')
  const navigate = useNavigate()
  const clientId = getClientId()

  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState('')
  const [success, setSuccess]   = useState('')

  const [form, setForm] = useState({
    state: '',
    mission: '',
    focus_areas: [],
    digest_frequency: 'weekly',
    typical_grant_min: '',
    typical_grant_max: '',
    org_ein: '',
    deadline_alerts_enabled: false,
  })

  // Plan tier drives the focus-area cap (backend enforces it on save).
  const [planTier, setPlanTier] = useState('starter')

  // Email deliverability metadata for the health banner.
  const [emailMeta, setEmailMeta] = useState({ email: '', status: 'ok', detail: null })
  // Unsubscribed state (digests paused via the email footer link).
  const [unsubscribed, setUnsubscribed] = useState(false)
  const [resubscribing, setResubscribing] = useState(false)

  useEffect(() => {
    if (!clientId) { navigate('/signin'); return }
    getPreferences()
      .then(prefs => {
        const tier = prefs.plan_tier || 'starter'
        setForm({
          state: prefs.state || '',
          mission: prefs.mission || '',
          focus_areas: prefs.focus_areas || [],
          // A stored 'daily' on a sub-Pro tier (pre-enforcement rows, or
          // after a downgrade) is served weekly by the scheduler and would
          // be rejected on save — show the truth and let the next save
          // normalize the row.
          digest_frequency:
            prefs.digest_frequency === 'daily' && !DAILY_DIGEST_TIERS.includes(tier)
              ? 'weekly'
              : prefs.digest_frequency || 'weekly',
          typical_grant_min: prefs.typical_grant_min != null ? String(prefs.typical_grant_min) : '',
          typical_grant_max: prefs.typical_grant_max != null ? String(prefs.typical_grant_max) : '',
          org_ein: prefs.org_ein || '',
          deadline_alerts_enabled: !!prefs.deadline_alerts_enabled,
        })
        setPlanTier(prefs.plan_tier || 'starter')
        setEmailMeta({
          email: prefs.email || '',
          status: prefs.email_status || 'ok',
          detail: prefs.email_status_detail || null,
        })
        setUnsubscribed(!!prefs.unsubscribed)
      })
      .catch(err => {
        // The API clears the cached session on a 401; bounce to sign-in.
        if (!getClientId()) { navigate('/signin'); return }
        setError(err.message)
      })
      .finally(() => setLoading(false))
  }, [clientId])

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }))
    setSuccess('')
    setError('')
  }

  const focusLimit = focusAreaLimit(planTier)
  // Grandfathered: stored areas can exceed the cap (e.g. after a downgrade —
  // data is never deleted). They still count for matching, but the backend
  // rejects any save until the selection is trimmed to the cap (or upgraded).
  const overCap = form.focus_areas.length > focusLimit

  function toggleFocus(code) {
    setForm(f => ({
      ...f,
      focus_areas: f.focus_areas.includes(code)
        ? f.focus_areas.filter(c => c !== code)
        : f.focus_areas.length < focusLimit ? [...f.focus_areas, code] : f.focus_areas,
    }))
    setSuccess('')
    setError('')
  }

  async function save(e) {
    e.preventDefault()
    if (form.focus_areas.length === 0) {
      setError('Please select at least one focus area.')
      return
    }
    const rangeError = grantRangeError(form.typical_grant_min, form.typical_grant_max)
    if (rangeError) {
      setError(rangeError)
      return
    }
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      await updatePreferences({
        state: form.state || undefined,
        mission: form.mission || undefined,
        focus_areas: form.focus_areas,
        digest_frequency: form.digest_frequency,
        typical_grant_min: Number(form.typical_grant_min),
        typical_grant_max: Number(form.typical_grant_max),
        // Sent even when blank, unlike the fields above: "" is how the API is
        // told to CLEAR a stored EIN. `undefined` would mean "leave it alone",
        // making a wrong EIN impossible to remove from this page.
        org_ein: form.org_ein,
        deadline_alerts_enabled: form.deadline_alerts_enabled,
      })
      setSuccess('Preferences saved! Your next digest will reflect these changes.')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Spinner />
    </div>
  )

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4"
        >
          ← Back to Dashboard
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Preferences</h1>
        <p className="text-gray-500 text-sm mt-1">
          Changes take effect on your next digest.
        </p>
      </div>

      <div className="mb-6">
        <EmailHealthBanner
          status={emailMeta.status}
          detail={emailMeta.detail}
          email={emailMeta.email}
        />
      </div>

      {unsubscribed && (
        <div className="mb-6 rounded-xl border border-yellow-300 bg-yellow-50 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-yellow-900">Digests are paused</p>
            <p className="text-xs text-yellow-800 mt-0.5">
              You unsubscribed from digest emails. Re-enable them to keep getting
              your weekly grant matches.
            </p>
          </div>
          <Button
            size="sm"
            loading={resubscribing}
            onClick={async () => {
              setResubscribing(true)
              setError('')
              try {
                await resubscribe()
                setUnsubscribed(false)
                setSuccess('Digests re-enabled. You\'ll be in the next Tuesday send.')
              } catch (err) {
                setError(err.message)
              } finally {
                setResubscribing(false)
              }
            }}
          >
            Resubscribe
          </Button>
        </div>
      )}

      <form onSubmit={save} className="space-y-8">
        <ErrorAlert message={error} />
        <SuccessAlert message={success} />

        {/* Location */}
        <section className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Location</h2>
          <div className="flex flex-col gap-1">
            <label htmlFor="prefs-state" className="text-sm font-medium text-gray-700">State</label>
            <select
              id="prefs-state"
              value={form.state}
              onChange={e => set('state', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">All states (nationwide grants only)</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <p className="text-xs text-gray-500">
              We'll show grants available in your state plus nationwide opportunities.
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Mission Statement</h2>
          <Input
            as="textarea"
            label="Describe your organization's work"
            rows={4}
            value={form.mission}
            onChange={e => set('mission', e.target.value)}
            placeholder="We support youth literacy programs in rural communities..."
            hint="This is the main thing we match on. Say who you serve, what you actually do, and where you work."
          />
          <MissionHints mission={form.mission} state={form.state} />
        </section>

        {/* Focus areas */}
        <section className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Focus Areas</h2>
            <span className="text-xs text-gray-500">
              {form.focus_areas.length}/{focusLimit === Infinity ? FOCUS_AREA_OPTIONS.length : focusLimit} selected
            </span>
          </div>
          {overCap && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3">
              <p className="text-xs text-yellow-900">
                Your {planTier} plan includes up to {focusLimit} focus areas. Your
                earlier selection of {form.focus_areas.length} still counts for
                matching, but saving requires trimming to {focusLimit}, or{' '}
                <Link to="/plans" className="font-medium underline">upgrading your plan</Link>.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FOCUS_AREA_OPTIONS.map(({ code, label }) => {
              const selected = form.focus_areas.includes(code)
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleFocus(code)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm
                    text-left transition-colors
                    ${selected
                      ? 'border-brand-500 bg-brand-100 text-brand-900 font-medium'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-brand-300'
                    }`}
                >
                  <span className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center
                    ${selected ? 'border-brand-500 bg-brand-500' : 'border-gray-300'}`}>
                    {selected && <span className="text-white text-xs leading-none">✓</span>}
                  </span>
                  {label}
                </button>
              )
            })}
          </div>
        </section>

        {/* Digest settings */}
        <section className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Digest Settings</h2>
          {/* fieldset/legend, not a bare <label>: this names the radio *group*,
              and a label may only name a single control (WCAG 1.3.1). */}
          <fieldset className="flex flex-col gap-1">
            <legend className="text-sm font-medium text-gray-700">Digest frequency</legend>
            <div className="flex gap-3">
              {['weekly', 'daily'].map(freq => {
                const dailyLocked = freq === 'daily' && !DAILY_DIGEST_TIERS.includes(planTier)
                return (
                  <label
                    key={freq}
                    className={`flex items-center gap-2 ${dailyLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  >
                    <input
                      type="radio"
                      name="freq"
                      value={freq}
                      disabled={dailyLocked}
                      checked={form.digest_frequency === freq}
                      onChange={() => set('digest_frequency', freq)}
                      className="accent-brand-500"
                    />
                    <span className="text-sm capitalize text-gray-700">{freq}</span>
                    {dailyLocked && (
                      <Link
                        to="/plans"
                        className="text-[10px] font-semibold uppercase tracking-wide text-brand-500
                          border border-brand-300 rounded-full px-2 py-0.5 hover:bg-brand-100"
                        title="The daily digest is available on Pro and Consultant plans"
                      >
                        Pro
                      </Link>
                    )}
                  </label>
                )
              })}
            </div>
            <p className="text-xs text-gray-500">
              Weekly digests are sent every Tuesday at 8am. The daily digest is
              available on Pro and Consultant plans.
            </p>
          </fieldset>

          <div className="grid grid-cols-2 gap-4">
            {/* Required, and not clearable (#103): see Onboard.jsx. */}
            <Input
              label="Typical grant min ($)"
              type="number"
              min="0"
              required
              value={form.typical_grant_min}
              onChange={e => set('typical_grant_min', e.target.value)}
              hint="The smallest grant you usually apply for."
            />
            <Input
              label="Typical grant max ($)"
              type="number"
              min="1"
              required
              value={form.typical_grant_max}
              onChange={e => set('typical_grant_max', e.target.value)}
              hint="The largest. We rank foundations by what they actually give."
            />
          </div>

          {/*
            OFF by default (migration 0019). Tier grants access; this grants
            consent. A second daily email stream is not covered by the double
            opt-in that authorised the weekly digest, so nobody is enrolled
            without asking — they come here and turn it on.
          */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.deadline_alerts_enabled}
              onChange={e => set('deadline_alerts_enabled', e.target.checked)}
              className="mt-0.5 accent-brand-500"
            />
            <span>
              <span className="text-sm font-medium text-gray-700">
                Email me when a deadline is approaching
              </span>
              <span className="block text-xs text-gray-500">
                A separate daily email, only when something you were shown is
                closing soon. Off unless you turn it on. Available on Growth and
                above.
              </span>
            </span>
          </label>

          {/*
            Optional, and the hint says why it is worth filling in — an EIN is
            the kind of field people skip unless told what it buys. It is not a
            matching input, so a blank one costs no leads; it only unlocks the
            "have they funded you before?" line on a foundation profile.
          */}
          <Input
            label="Your organization's EIN"
            value={form.org_ein}
            onChange={e => set('org_ein', e.target.value)}
            placeholder="NN-NNNNNNN"
            hint="Optional. Lets us tell you when a foundation has already funded you"
          />
        </section>

        <div className="flex justify-end">
          <Button type="submit" loading={saving} size="lg">
            Save Preferences
          </Button>
        </div>
      </form>
    </div>
  )
}
